import { useContext, useState, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { CurrentDataContext } from 'src/app/current-data';
import { fetchWorkspacePlan } from 'src/sections/repository/actions/fetch-repository-details';
import {
  getWorkspacePlan,
  getWorkspacePlanRequestStatus,
} from 'src/selectors/workspace-selectors';

import messages from '../i18n/rovo-dev-agents-page.i18n';
import { jiraSitesResource } from '../resources/jira-sites-resource';

type RovoDevAgentsRequirements = {
  loading: boolean;
  error: FormattedMessage.MessageDescriptor | null;
  isWorkspacePaid: boolean;
  hasConnectedJiraSite: boolean;
  hasEnabledRovoDevAgents: boolean;
};

export const useRovoDevAgentsRequirements = (): RovoDevAgentsRequirements => {
  const [error, setError] = useState<FormattedMessage.MessageDescriptor | null>(
    null
  );
  const { workspace } = useContext(CurrentDataContext);
  const dispatch = useDispatch();

  const {
    data: jiraSitesData,
    loading: jiraSitesLoading,
    error: jiraSitesError,
  } = useResource(jiraSitesResource);

  const { isFetchWorkspacePlanLoading, isFetchWorkspacePlanError } =
    useSelector(getWorkspacePlanRequestStatus);
  const { isWorkspacePaid, isWorkspacePremium } = useSelector(getWorkspacePlan);

  useEffect(() => {
    if (workspace?.uuid && isFetchWorkspacePlanLoading) {
      dispatch(fetchWorkspacePlan(workspace.uuid));
    }
  }, [dispatch, isFetchWorkspacePlanLoading, workspace?.uuid]);

  if (isFetchWorkspacePlanError && !error) {
    setError(messages.permissionsError);
  }

  if (jiraSitesError) {
    setError(messages.permissionsError);
  }

  const hasConnectedJiraSite =
    jiraSitesData?.some(site => site.connected) ?? false;
  const hasEnabledRovoDevAgents =
    jiraSitesData?.some(site => site.isRovoDevAgentsEnabled) ?? false;

  return {
    loading: isFetchWorkspacePlanLoading || jiraSitesLoading,
    error,
    isWorkspacePaid: isWorkspacePaid || isWorkspacePremium,
    hasConnectedJiraSite,
    hasEnabledRovoDevAgents,
  };
};
